import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePermisos from '../hooks/usePermisos';
import {
  Building,
  Building2,
  BedDouble,
  Clock,
  Bed,
  Share2,
  CreditCard,
  Users,
  Bell,
  Package,
  FileText,
  ChevronRight,
  Hash,
  Shield,
  Key,
  Link2
} from 'lucide-react';
import ModalDatosHotel from '../components/configuracion/ModalDatosHotel';
import ModalParametrosGenerales from '../components/configuracion/ModalParametrosGenerales';
import ModalMetodosPago from '../components/configuracion/ModalMetodosPago';
import ModalTiposHabitacion from '../components/configuracion/ModalTiposHabitacion';
import ModalCanalesReserva from '../components/configuracion/ModalCanalesReserva';
import ModalUsuarios from '../components/configuracion/ModalUsuarios';
import ModalClientes from '../components/configuracion/ModalClientes';
import ModalNotificaciones from '../components/configuracion/ModalNotificaciones';
import ModalConsecutivos from '../components/configuracion/ModalConsecutivos';
import ModalPermisos from '../components/configuracion/ModalPermisos';
import PasswordModal from '../components/configuracion/PasswordModal';
import ModalTRA from '../components/configuracion/ModalTRA';
import './Configuracion.css';

function Configuracion() {
  const navigate = useNavigate();
  const [modalActivo, setModalActivo] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const { tienePermiso, rol: rolUsuario } = usePermisos();

  const configCards = [
    {
      id: 'datos-hotel',
      titulo: 'Datos del Hotel',
      descripcion: 'Información legal, NIT, DIAN y facturación electrónica',
      icon: <Building size={28} />,
      color: 'primary',
      permiso: 'configuracion.datos_hotel',
      onClick: () => setModalActivo('datos-hotel')
    },
    {
      id: 'mi-licencia',
      titulo: 'Mi Licencia',
      descripcion: 'Estado de licencia, activación y módulos habilitados',
      icon: <Key size={28} />,
      color: 'violet',
      permiso: 'configuracion.datos_hotel',
      onClick: () => navigate('/mi-licencia')
    },
    {
      id: 'parametros',
      titulo: 'Parámetros Generales',
      descripcion: 'Horarios, políticas, IVA y configuraciones operativas',
      icon: <Clock size={28} />,
      color: 'success',
      permiso: 'configuracion.parametros',
      onClick: () => setModalActivo('parametros')
    },
    {
      id: 'tipos-habitacion',
      titulo: 'Tipos de Habitación',
      descripcion: 'Gestionar catálogo de tipos, capacidades y precios base',
      icon: <Bed size={28} />,
      color: 'info',
      permiso: 'configuracion.habitaciones',
      onClick: () => setModalActivo('tipos-habitacion')
    },
    {
      id: 'habitaciones',
      titulo: 'Gestión de Habitaciones',
      descripcion: 'Crear, editar y administrar habitaciones del hotel',
      icon: <BedDouble size={28} />,
      color: 'primary',
      permiso: 'configuracion.habitaciones',
      onClick: () => navigate('/habitaciones')
    },
    {
      id: 'canales-reserva',
      titulo: 'Canales de Reserva',
      descripcion: 'Configurar canales, comisiones e integraciones',
      icon: <Share2 size={28} />,
      color: 'secondary',
      permiso: 'configuracion.parametros',
      onClick: () => setModalActivo('canales-reserva')
    },
    {
      id: 'metodos-pago',
      titulo: 'Métodos de Pago',
      descripcion: 'Activar o desactivar métodos de pago disponibles',
      icon: <CreditCard size={28} />,
      color: 'warning',
      permiso: 'configuracion.metodos_pago',
      onClick: () => setModalActivo('metodos-pago')
    },
    {
      id: 'usuarios',
      titulo: 'Usuarios y Roles',
      descripcion: 'Gestionar usuarios, permisos y contraseñas',
      icon: <Users size={28} />,
      color: 'danger',
      permiso: 'configuracion.usuarios',
      onClick: () => setModalActivo('usuarios')
    },
    {
      id: 'clientes',
      titulo: 'Gestión de Clientes',
      descripcion: 'Registro maestro de clientes con datos DIAN',
      icon: <Users size={28} />,
      color: 'info',
      permiso: 'all',
      onClick: () => setModalActivo('clientes')
    },
    {
      id: 'notificaciones',
      titulo: 'Notificaciones',
      descripcion: 'Email, SMS, WhatsApp y triggers de notificaciones',
      icon: <Bell size={28} />,
      color: 'purple',
      permiso: 'configuracion.parametros',
      onClick: () => setModalActivo('notificaciones')
    },
    {
      id: 'consecutivos',
      titulo: 'Consecutivos',
      descripcion: 'Resoluciones DIAN y numeración de documentos electrónicos',
      icon: <Hash size={28} />,
      color: 'teal',
      permiso: 'configuracion.facturacion',
      onClick: () => setModalActivo('consecutivos')
    },
    {
      id: 'permisos',
      titulo: 'Gestión de Permisos',
      descripcion: 'Configurar permisos por rol y personalizados por usuario',
      icon: <Shield size={28} />,
      color: 'violet',
      permiso: 'configuracion.permisos',
      onClick: () => setModalActivo('permisos')
    },
    {
      id: 'tra',
      titulo: 'TRA - MinCIT',
      descripcion: 'Tarjeta de Registro de Alojamiento (obligatorio)',
      icon: <FileText size={28} />,
      color: 'teal',
      permiso: 'configuracion.datos_hotel',
      onClick: () => setModalActivo('tra')
    },
    {
      id: 'inventario',
      titulo: 'Inventario',
      descripcion: 'Gestión de productos, categorías y movimientos',
      icon: <Package size={28} />,
      color: 'pink',
      permiso: 'all',
      wide: true,
      onClick: () => navigate('/productos')
    },
    {
      id: 'gestion-propiedades',
      titulo: 'Gestión de Propiedades',
      descripcion: 'Crear, editar y asignar propiedades del sistema',
      icon: <Building2 size={28} />,
      color: 'violet',
      permiso: 'superadmin',
      onClick: () => navigate('/gestion-propiedades')
    },
  ];

  if (!isAuthenticated) {
    return <PasswordModal onSuccess={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="configuracion-page">
      <div className="configuracion-header">
        <h1 className="configuracion-title">Configuración del Sistema</h1>
        <p className="configuracion-subtitle">
          Administra todos los aspectos de la configuración del hotel desde un solo lugar
        </p>
      </div>

      <div className="config-grid">
        {configCards
          .filter(card => {
            if (card.permiso === 'superadmin') return rolUsuario === 'superadmin';
            if (card.permiso === 'all') return true;
            return tienePermiso(card.permiso);
          })
          .map(card => (
          <div
            key={card.id}
            className={`config-card ${card.wide ? 'config-card--wide' : ''}`}
            onClick={card.onClick}
          >
            <div className="config-card__header">
              <div className={`config-card__icon config-card__icon--${card.color}`}>
                {card.icon}
              </div>
              <div className="config-card__content">
                <h3 className="config-card__title">{card.titulo}</h3>
                <p className="config-card__description">{card.descripcion}</p>
              </div>
            </div>

            <div className="config-card__footer">
              <div className="config-card__action">
                <span>Configurar</span>
                <ChevronRight size={16} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modales de Configuración */}
      {modalActivo === 'datos-hotel' && (
        <ModalDatosHotel onClose={() => setModalActivo(null)} />
      )}

      {modalActivo === 'parametros' && (
        <ModalParametrosGenerales onClose={() => setModalActivo(null)} />
      )}

      {modalActivo === 'metodos-pago' && (
        <ModalMetodosPago onClose={() => setModalActivo(null)} />
      )}

      {modalActivo === 'tipos-habitacion' && (
        <ModalTiposHabitacion onClose={() => setModalActivo(null)} />
      )}

      {modalActivo === 'canales-reserva' && (
        <ModalCanalesReserva onClose={() => setModalActivo(null)} />
      )}

      {modalActivo === 'usuarios' && (
        <ModalUsuarios onClose={() => setModalActivo(null)} />
      )}

      {modalActivo === 'clientes' && (
        <ModalClientes onClose={() => setModalActivo(null)} />
      )}

      {modalActivo === 'notificaciones' && (
        <ModalNotificaciones onClose={() => setModalActivo(null)} />
      )}

      {modalActivo === 'consecutivos' && (
        <ModalConsecutivos onClose={() => setModalActivo(null)} />
      )}

      {modalActivo === 'permisos' && (
        <ModalPermisos onClose={() => setModalActivo(null)} />
      )}

      {modalActivo === 'tra' && (
        <ModalTRA onClose={() => setModalActivo(null)} />
      )}

      {/* Placeholder para cualquier otro modal no implementado */}
      {modalActivo && !['datos-hotel', 'parametros', 'metodos-pago', 'tipos-habitacion', 'canales-reserva', 'usuarios', 'clientes', 'notificaciones', 'consecutivos', 'permisos', 'tra'].includes(modalActivo) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setModalActivo(null)}
        >
          <div
            style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '12px',
              maxWidth: '500px',
              textAlign: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Módulo: {modalActivo}</h2>
            <p style={{ color: '#6b7280', marginTop: '1rem' }}>
              Este modal será implementado próximamente
            </p>
            <button
              onClick={() => setModalActivo(null)}
              style={{
                marginTop: '1.5rem',
                padding: '0.75rem 1.5rem',
                background: '#1e40af',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Configuracion;
